import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import groupBy from 'lodash.groupby';
import { InputSelect } from '@bluevalet/common-ui';
import Classnames from 'classnames';
import { ThunkDispatch } from 'redux-thunk';

import { getBusiness, getTravelType } from '../reducers/cart';
import { getCompanies } from '../reducers/business';
import { persistCart, updateTravelType } from '../actions/cart';
import { fetchDetails } from '../actions/information';
import { fetchServices } from '../actions/service';
import { TravelTypes, BusinessCompany } from '../types';
import { State } from '../reducers';

interface Props {
  forceValidation?: boolean;
}

const getCustomer = (companies: Array<BusinessCompany>, id?: number | string) =>
  companies.find(c => c.businessCustomerId?.toString() === id?.toString());

const TravelType: FC<Props> = ({ forceValidation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<State, unknown, any>>();

  const travelType = useSelector(getTravelType) as string | undefined;
  const business = useSelector(getBusiness) as string | undefined;
  const companies = useSelector(getCompanies) as Array<BusinessCompany>;

  const [businessId, setBusinessId] = useState<string | undefined>();

  const isTravelTypeChecked = (type: string) => type === travelType;
  const isCompanyActive = (company: BusinessCompany) => company.businessId.toString() === businessId;
  const groupedCompanies = useMemo(
    () => groupBy(companies, 'businessId') as Record<string, BusinessCompany[]>,
    [companies],
  );

  useEffect(() => {
    const elem = getCustomer(companies, business);
    setBusinessId(elem?.businessId.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  const onTravelTypeChange = (type: string, businessCustomerId?: string, businessIdParam?: string) => {
    setBusinessId(businessIdParam);

    const getWorker = () => {
      if (businessIdParam && businessCustomerId) {
        const elem = getCustomer(companies, businessCustomerId);
        if (elem) {
          return {
            id: businessCustomerId,
            name: elem.workerName ?? '',
            business: elem.businessName,
            isWorker: elem.isWorker,
          };
        }
      }
      return undefined;
    };

    dispatch(updateTravelType(type, businessCustomerId, getWorker()));
    dispatch(persistCart([fetchDetails, fetchServices]));
  };

  const onWorkerSelect = (businessCustomerId: string) => {
    const elem = getCustomer(companies, businessCustomerId);
    if (elem) {
      setBusinessId(elem.businessId.toString());
      dispatch(
        updateTravelType(TravelTypes.bvb, businessCustomerId, {
          id: businessCustomerId,
          name: elem.workerName ?? '',
          business: elem.businessName,
          isWorker: elem.isWorker,
        }),
      );
      dispatch(persistCart([fetchDetails, fetchServices]));
    }
  };

  const cls = Classnames('tripsType', { error: forceValidation && !travelType });

  return (
    <div className="booking-trip-kind">
      <fieldset id="FieldsetTravelType">
        <legend>{t('booking-trip_type')}</legend>
        <div className="form">
          <div className={cls}>
            <div className="radio">
              <label htmlFor="kind_perso">
                <input
                  type="radio"
                  id="kind_perso"
                  name="travelType"
                  value={TravelTypes.perso}
                  onChange={() => onTravelTypeChange(TravelTypes.perso)}
                  checked={isTravelTypeChecked(TravelTypes.perso)}
                />
                <span className="checkmark" />
                {t('booking-kind-personal')}
              </label>
            </div>

            {(!companies || companies.length === 0) && (
              <div className="radio">
                <label htmlFor="kind_pro">
                  <input
                    type="radio"
                    id="kind_pro"
                    name="travelType"
                    value={TravelTypes.pro}
                    onChange={() => onTravelTypeChange(TravelTypes.pro)}
                    checked={isTravelTypeChecked(TravelTypes.pro)}
                  />
                  <span className="checkmark" />
                  {t('booking-kind-professional')}
                </label>
              </div>
            )}

            {companies &&
              companies.length > 0 &&
              Object.values(groupedCompanies).map((workers, i) => (
                <div className="radio" key={workers[0].businessId}>
                  <label htmlFor={`kind_bvb_${i}`}>
                    <input
                      type="radio"
                      id={`kind_bvb_${i}`}
                      name="business"
                      value={`${TravelTypes.bvb}_${workers[0].businessId}`}
                      onChange={() =>
                        onTravelTypeChange(
                          TravelTypes.bvb,
                          workers[0].businessCustomerId.toString(),
                          workers[0].businessId.toString(),
                        )
                      }
                      checked={isCompanyActive(workers[0])}
                    />
                    <span className="checkmark" />
                    <Trans i18nKey="booking-kind-business" values={{ label: workers[0].businessName }} />
                  </label>
                  {workers.length > 1 && (
                    <div className="WorkerSelectorContainer">
                      <div className="WorkerSelectorPrefix">{t('booking-kind-business-book-for')}</div>
                      <div className="WorkerSelector">
                        <InputSelect
                          id={`worker-business-${workers[0].businessId}`}
                          name="worker"
                          value={businessId === workers[0].businessId?.toString() ? business : undefined}
                          onChange={onWorkerSelect}
                          hidePlaceholderOption>
                          {workers.map(w => (
                            <option key={w.businessCustomerId} value={w.businessCustomerId.toString()}>
                              {w.workerName}
                            </option>
                          ))}
                        </InputSelect>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default TravelType;
